import { Children, Component } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

type ComponentProps = {
  children?: any;
  spacing: string;
  renderSegment: (segment: any) => JSX.Element;
  pickSegments: (child: string) => any[];
};

export const PaddedSegment = styled.span<{ spacing: string }>(props => ({
  '& + &': {
    marginLeft: props.spacing,
  },
}));

/*
 * Adds spacing between text by wrapping them in elements.
 *
 * Example:
 *
 * const Title = styled.h1`...`;
 * const PaddedTitle = withPadding(Title);
 * <PaddedTitle>bitbucket/bitkit</PaddedTitle>
 *
 * will output the following HTML:
 *
 * <h1>
 *  <span>bitbucket</span>
 *  <span>/</span>
 *  <span>bitkit</span>
 * </h1>
 *
 * where each span has CSS to add a margin between them
 */
export default function (WrappedComponent: any) {
  return class WithPadding extends Component<ComponentProps> {
    static defaultProps = {
      /* eslint react/default-props-match-prop-types: "warn" */
      renderSegment: (s: any) => s,
      pickSegments: (path: string) => path.split(new RegExp('(/)', 'g')),
      spacing: '0.5em',
    };

    render() {
      const child = this.props.children;
      if (!child || Children.count(child) !== 1 || typeof child !== 'string') {
        return <WrappedComponent>{child}</WrappedComponent>;
      }

      // Split the segments and let the consumer pick which to show
      const segments = this.props.pickSegments(child);

      // Wrap each part of the split in a PaddedSegment
      const wrappedSegments = segments.map((segment, idx) => (
        <PaddedSegment key={idx} spacing={this.props.spacing}>
          {this.props.renderSegment(segment)}
        </PaddedSegment>
      ));

      return <WrappedComponent>{wrappedSegments}</WrappedComponent>;
    }
  };
}
